/**
 * useProjectSwitch — the one place that knows how to move the session into
 * another workspace.
 *
 * A workspace partitions every resource in the account — streams, VOD,
 * playlists, destinations, showcases, transcoders, team — and those lists are
 * held by a dozen independent stores and views. After the server has switched
 * the session, a full page load is the only way to guarantee none of them is
 * left showing the previous workspace. Two details make that reload feel
 * deliberate rather than jarring:
 *
 *  - If the current URL points at one specific resource (a stream, a folder)
 *    it will not exist in the other workspace, so the reload lands on the
 *    dashboard instead of on a "not found". Listing pages reload in place.
 *  - The switch is remembered in sessionStorage so the fresh page can greet the
 *    user with "Switched to X" — otherwise the reload looks like nothing
 *    happened until they notice the sidebar.
 *
 * State is module-scoped so the overlay rendered by the sidebar reflects a
 * switch started from a modal.
 */
import { ref, readonly } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { APP_EVENTS, emitAppEvent } from '@/Utils/appEventBus'
import { track, EVENTS } from '@/lib/tracking'

const MEMO_KEY = 'castr:projectSwitch'
// A memo older than this is stale (the reload never happened, or a tab was
// restored much later) and must not produce a confusing toast.
const MEMO_TTL_MS = 60 * 1000

const switching = ref(false)
const pendingProject = ref(null)

function rememberSwitch(project, { created = false } = {}) {
  try {
    window.sessionStorage.setItem(MEMO_KEY, JSON.stringify({
      id: project.id,
      name: project.name,
      color: project.color,
      created,
      at: Date.now(),
    }))
  } catch (e) { /* storage unavailable: the toast is a nicety, not a requirement */ }
}

/**
 * Reads and clears the memo left before the reload. Returns null when there is
 * nothing to announce.
 */
export function consumeProjectSwitchMemo() {
  try {
    const raw = window.sessionStorage.getItem(MEMO_KEY)
    if (!raw) return null
    window.sessionStorage.removeItem(MEMO_KEY)
    const memo = JSON.parse(raw)
    if (!memo || Date.now() - (memo.at || 0) > MEMO_TTL_MS) return null
    return memo
  } catch (e) {
    return null
  }
}

export function useProjectSwitch() {
  const store = useStore()
  const route = useRoute()
  const router = useRouter()

  const reloadIntoProject = () => {
    // Parameterised routes address one resource of the workspace being left.
    const hasParams = Object.keys(route?.params || {}).length > 0
    if (hasParams) {
      window.location.assign(router.resolve({ path: '/dashboard' }).href)
    } else {
      window.location.reload()
    }
  }

  /**
   * @param {{ id: string, name: string, color?: string }} project
   * @param {{ created?: boolean }} [options]
   * @returns {Promise<boolean>} whether the switch was started
   */
  const switchTo = async (project, { created = false } = {}) => {
    if (!project?.id || switching.value) return false

    const activeId = store.getters['Projects/activeProject']?.id
    if (project.id === activeId) return false

    switching.value = true
    pendingProject.value = project
    emitAppEvent(APP_EVENTS.PROJECT_CHANGING, project.id)
    store.dispatch('Ui/setLoadingStatus', true)

    try {
      await store.dispatch('Projects/selectProject', project.id)
      emitAppEvent(APP_EVENTS.PROJECT_CHANGED, project.id)
      track(EVENTS.PROJECT_SWITCHED, { after_create: created })
      rememberSwitch(project, { created })
      reloadIntoProject()
      return true
    } catch (error) {
      console.error('[Projects] Failed to switch workspace:', error)
      store.dispatch('Ui/setLoadingStatus', false)
      switching.value = false
      pendingProject.value = null
      throw error
    }
  }

  return {
    switching: readonly(switching),
    pendingProject: readonly(pendingProject),
    switchTo,
  }
}
